import { api } from 'api';
import { getNumberUnmaskStr } from 'components/FormEditReceiver/func';
import { DEBOUNCE_SEC, DESKTOP_MIN_WIDTH, OBJECT_DEFAULT } from 'constants';
import { formatter } from 'helpers';
import { Fade } from 'helpers/animation';
import { checkMaintenance } from 'helpers/maintenance';
import useFormInput from 'hooks/form/useFormInput';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useLang from 'hooks/useLang';
import useStore from 'hooks/useStore';
import { useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import { SET_SHOW_LOADING } from 'store/action';
import styled from 'styled-components';
import { numberUtils, stringUtils } from 'utils';
import { checkFormInputValidation } from '../../func';

const FormInputMobileNumberValidation = ({
  ref,
  field,
  config,
  numberMaskStr,
  setNumberMaskStr,
  information,
  setInformation,
  validation,
  setValidation,
  rootStyles = OBJECT_DEFAULT,
  validationStyles = OBJECT_DEFAULT,
  // Additive, optional. Default is a no-op, so callers that don't pass
  // these (all current callers except the PayTo mobile field) see
  // byte-identical behavior: shouldApplyPhoneValidation always allows the
  // write, and onPhoneFormatValidated does nothing.
  onPhoneFormatValidated = () => {},
  shouldApplyPhoneValidation = () => true,
  // DEV-1401: additive, optional, default false. When true (only the
  // PayTo mobile field passes true), a successful phone-format check that
  // is about to call onPhoneFormatValidated hands loading ownership off to
  // whatever onPhoneFormatValidated triggers (validatePayId), instead of
  // this component clearing loading itself -- see checkNumberValid below.
  hasSequencedLoadingHandoff = false,
  // Additive, optional. Default is a no-op, so callers that don't pass it
  // (all current callers except the PayTo mobile field) see byte-identical
  // behavior. When true, handleOnBlur becomes a complete no-op for
  // validation purposes -- used by the PayTo mobile field so blur never
  // clears an existing (possibly async validatePayId-derived) message.
  suppressBlurValidation = false,
  // DEV-1401: additive, optional, default false. When true (only the PayTo
  // PayID mobile field passes true, and only while its bank-validation
  // failure row is shown), the inline feedback text renders empty instead
  // of the mapped message -- isInvalid/styling and validation truthiness
  // are unaffected, so blocking/gating behavior stays exactly as before.
  suppressMessage = false,
  // DEV-1442: additive, optional, no-op by default (only the PayTo mobile
  // field passes it). Called on every non-empty keystroke that passes the
  // max-length/numeric filters, with the unmasked value and whether it is
  // ready for phone validation, so the caller can
  // supersede a pending check when the input has become invalid/incomplete.
  onPhoneInputChange = () => {},
}) => {
  const { t } = useLang();
  const { dispatch } = useStore();

  const isConfigRequired = config?.required;
  const isConfigVisible = config?.visible;
  const placeholder = config?.example;
  const numberFormat = formatter.formatParse(config?.format);
  const prefixFormat = formatter.formatParse(config?.prefix);
  const numberMaxLength = config?.max;
  const numberMinLength = config?.min;

  const {
    inputRef,
    resetFormInput,
    getValueTrim,
    FormLabel,
    FormControlFeedback,
    setInformationFormInput,
    setValidationFormInput,
  } = useFormInput(field);

  useEffect(() => {
    if (!information[field.name]) return;
    if (numberMaskStr) return;
    if (information[field.name]) {
      const numberMask = formatter.mask(
        getNumberUnmaskStr(information[field.name], numberFormat),
        numberFormat
      );
      setNumberMaskStr(numberMask);
    }
  }, [information, numberMaskStr]);

  const handleNumberValidation = async (value, name, validation) => {
    // DEV-1401: SET_SHOW_LOADING:false no longer dispatched here. Whether
    // this call's completion should clear loading depends on whether
    // onPhoneFormatValidated is about to be called for this value (decided
    // in checkNumberValid below, after this promise resolves) -- clearing
    // it unconditionally here caused a visible false->true flicker on the
    // PayTo mobile field, where a further validatePayId call starts right
    // after this one finishes.
    try {
      const { data } = await api.validatePhone('AU', value);

      if (data) {
        const isValid = data.data.status === 'valid';

        validation = {
          ...validation,
          [name]: isValid ? '' : field.msgNumberInvalid,
        };

        return validation;
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
    }

    return validation;
  };
  const checkNumberValid = async (value, validation) => {
    // DEV-1442: a pending debounce armed for an earlier value must not call
    // validatePhone once the input has moved on (default predicate is always
    // true, so every other caller is unchanged).
    if (!shouldApplyPhoneValidation(value)) {
      return;
    }

    validation = await handleNumberValidation(value, field.name, validation);

    // Stale-response guard (payto/phone validator race fix): when a caller
    // supplies shouldApplyPhoneValidation (only the PayTo mobile field
    // does), a response for a value that's already been superseded is
    // dropped instead of being written -- this is what stops this write
    // from clobbering a fresher validatePayId result for the current
    // value. Default predicate always returns true, so this is an
    // unconditional write for every other caller, same as before.
    //
    // DEV-1401: a stale/superseded response must not touch loading either
    // -- a newer value's own keystroke has already armed its own
    // true/false cycle, and this response has nothing to do with it. This
    // early return only ever fires for the PayTo mobile field (the only
    // caller with a non-default predicate), so no other caller's loading
    // handling changes here.
    if (!shouldApplyPhoneValidation(value)) {
      return;
    }

    setValidation(validation);

    if (!validation[field.name]) {
      onPhoneFormatValidated(value);
      // DEV-1401: only clear loading here when nothing downstream is about
      // to own it. For the PayTo mobile field (hasSequencedLoadingHandoff),
      // onPhoneFormatValidated just armed validatePayId, whose own
      // finally-block clear (withCheckMobileNumber.jsx) is the sole owner
      // of the false transition from here on. For every other caller,
      // onPhoneFormatValidated is a no-op and this is the only place left
      // to clear loading.
      if (!hasSequencedLoadingHandoff) {
        dispatch({ type: SET_SHOW_LOADING, payload: false });
      }
    } else {
      // Phone-format validation determined an actual invalid-format message
      // for this value -- no downstream call will run, so this is the
      // terminal clear regardless of hasSequencedLoadingHandoff. Note: a
      // thrown/caught validatePhone error does NOT land here -- the catch
      // block above returns `validation` unmutated, so it still takes the
      // `if` branch above (pre-existing behavior, unchanged by this diff).
      dispatch({ type: SET_SHOW_LOADING, payload: false });
    }
  };
  const { debounce: handleNumberValid } = useDebounceCallback(
    (value, validation) => {
      checkNumberValid(value, validation);
    },
    DEBOUNCE_SEC
  );

  const handleOnChange = async (e) => {
    let { value, name } = e.target;
    const valueTrim = getValueTrim(value);

    const numberUnmaskStr = getNumberUnmaskStr(valueTrim, numberFormat);

    if (numberUnmaskStr?.length > numberMaxLength) return;

    if (field.type === 'tel' && !numberUtils.isNumber(numberUnmaskStr)) return;

    if (!numberUnmaskStr) {
      // resetFormInput({
      //   information,
      //   setInformation,
      //   validation,
      //   setValidation,
      // });
      setNumberMaskStr('');
      let newInformation = setInformationFormInput({
        value: '',
        information,
        setInformation,
      });
      setValidationFormInput({
        validation,
        setValidation,
        checkFormInputValidation: () =>
          checkFormInputValidation(
            newInformation[name],
            field,
            newInformation,
            validation
          ),
      });
      return;
    }

    let newInformation = {
      ...information,
    };

    let newValidation = {
      ...validation,
    };

    const numberStr = formatter.mask(numberUnmaskStr, numberFormat);
    setNumberMaskStr(numberStr);

    newValidation = {
      ...newValidation,
      [name]: '',
    };

    if (prefixFormat !== '') {
      if (Array.isArray(prefixFormat) && prefixFormat?.length > 1) {
        if (!prefixFormat?.includes(numberUnmaskStr[0])) {
          newValidation = {
            ...newValidation,
            [name]: field.msgStartWith2,
          };
        }
      }

      if (
        !Array.isArray(prefixFormat) ||
        (Array.isArray(prefixFormat) && prefixFormat?.length === 1)
      ) {
        const prefixMask = formatter.mask(prefixFormat, prefixFormat);

        if (prefixMask?.length > 1) {
          if (numberUnmaskStr?.slice(0, 2) !== prefixMask) {
            newValidation = {
              ...newValidation,
              [name]: field.msgStartWith,
            };
          }
        }

        if (prefixMask?.length === 1) {
          if (numberUnmaskStr[0] !== prefixMask) {
            newValidation = {
              ...newValidation,
              [name]: field.msgStartWith,
            };
          }
        }
      }
    }

    const isCheckNumber =
      (numberUnmaskStr?.length === numberMaxLength ||
        numberUnmaskStr?.length === numberMinLength) &&
      !newValidation[field.name];
    onPhoneInputChange(numberUnmaskStr, isCheckNumber);
    if (isCheckNumber) {
      dispatch({ type: SET_SHOW_LOADING, payload: true });

      newInformation = {
        ...newInformation,
        [name]: numberUnmaskStr,
      };
      setInformation(newInformation);

      handleNumberValid(numberUnmaskStr, newValidation);
    }

    setValidation(newValidation);
  };

  const handleOnBlur = (e) => {
    if (suppressBlurValidation) return;

    let newValidation = { ...validation };

    const numberUnmaskStr = getNumberUnmaskStr(numberMaskStr, numberFormat);

    if (isConfigVisible && numberUnmaskStr && !validation[field.name]) {
      if (
        (numberUnmaskStr.length > 0 &&
          numberUnmaskStr.length < numberMinLength) ||
        (numberUnmaskStr.length > numberMinLength &&
          numberUnmaskStr.length < numberMaxLength)
      ) {
        newValidation = {
          ...newValidation,
          [field.name]: field.msgInvalid,
        };
      }

      setValidation(newValidation);

      return;
    }

    handleOnChange(e);
  };

  const getMessageValidation = () => {
    const numberUnmaskStr = getNumberUnmaskStr(numberMaskStr, numberFormat);

    if (config?.prefix !== '') {
      if (Array.isArray(prefixFormat) && prefixFormat?.length > 1) {
        const prefixMask1 = formatter.mask(prefixFormat[0], prefixFormat[0]);
        const prefixMask2 = formatter.mask(prefixFormat[1], prefixFormat[1]);

        return stringUtils.replaceKeyword(t(validation[field.name]), [
          {
            key: 'prefix1',
            value: prefixMask1,
          },
          {
            key: 'prefix2',
            value: prefixMask2,
          },
        ]);
      }

      if (
        !Array.isArray(prefixFormat) ||
        (Array.isArray(prefixFormat) && prefixFormat?.length === 1)
      ) {
        return t(validation[field.name]);
      }
    }

    if (numberUnmaskStr?.length < numberMinLength) {
      return t(validation[field.name])?.replace(10, numberMinLength);
    }

    if (
      numberUnmaskStr?.length > numberMinLength &&
      numberUnmaskStr?.length < numberMaxLength
    ) {
      return t(validation[field.name])?.replace(10, numberMaxLength);
    }

    return t(validation[field.name]);
  };

  return (
    <FormGroupStyled style={rootStyles || {}}>
      <FormLabel isConfigRequired={isConfigRequired} />
      <FormControl
        ref={ref}
        type={field.type}
        name={field.name}
        value={numberMaskStr || ''}
        placeholder={placeholder || t(field.placeholder)}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        isInvalid={validation[field.name]}
        readOnly={field.isReadOnly}
        inputMode="numeric"
      />
      <FormControlFeedback
        value={suppressMessage ? '' : getMessageValidation()}
      />
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  margin-bottom: 32px;
  position: relative;
  ${Fade};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 34px;
  }
`;

const FormControl = styled(Form.Control)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;

  color: var(--ds-c-grey-dark);
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 12px;
  padding-inline: 10px;
  background: var(--ds-c-white);

  &:focus {
    background: var(--ds-c-white) !important;
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
    padding-inline: 10px !important;
  }

  &[readonly] {
    background: var(--ds-c-white) !important;
    border: none !important;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    text-transform: none;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export default FormInputMobileNumberValidation;
